import { useGenerationLeaveGuard } from '@/hooks/useGenerationLeaveGuard';
import GenerationLeaveModal from '@/pages/chat-page/components/generation-leave-modal';
import {
  getGenerationGuardBlockedPath,
  selectIsGenerationGuardBlocked,
  useChatStore,
} from '@/store/useChatStore';
import { createContext, useCallback, useContext, useMemo } from 'react';

type GenerationGuardContextValue = {
  guardAction: (action: () => void) => void;
};

const GenerationGuardContext = createContext<GenerationGuardContextValue | null>(
  null
);

export function GenerationGuardProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const isBlocked = useChatStore(selectIsGenerationGuardBlocked);

  const getIsBlocked = useCallback(
    () => selectIsGenerationGuardBlocked(useChatStore.getState()),
    []
  );

  const getBlockedPath = useCallback(
    () => getGenerationGuardBlockedPath(useChatStore.getState()),
    []
  );

  const { isLeaveModalOpen, handleStay, handleLeave, guardAction } =
    useGenerationLeaveGuard(isBlocked, getIsBlocked, getBlockedPath);

  const value = useMemo(() => ({ guardAction }), [guardAction]);

  return (
    <GenerationGuardContext.Provider value={value}>
      {children}
      <GenerationLeaveModal
        isOpen={isLeaveModalOpen}
        onStay={handleStay}
        onLeave={handleLeave}
      />
    </GenerationGuardContext.Provider>
  );
}

export function useGenerationGuard() {
  const context = useContext(GenerationGuardContext);

  if (!context) {
    throw new Error(
      'useGenerationGuard must be used within GenerationGuardProvider'
    );
  }

  return context;
}
